import { useEffect, useState } from 'react';

import { isEmpty, isUndefined } from 'lodash-es';

import { IBackendCartEntries, ISanityImage } from '@rbi-ctg/menu';
import { CartEntryType } from 'generated/rbi-graphql';
import { IRootQuery } from 'generated/sanity-graphql';
import GetAllImages from 'remote/queries/all-images';
import { useLazySanityQuery } from 'state/graphql';
import { useLocale } from 'state/intl';
import { useLogger } from 'state/logger';
import { maybeGetPickerIdFromCartEntry } from 'utils/menu/maybe-get-picker-id-from-cart-entry';

declare interface ISanityImageMap {
  [key: string]: ISanityImage;
}

declare interface IIdsMap {
  [CartEntryType.COMBO]: string[];
  [CartEntryType.ITEM]: string[];
  [CartEntryType.OFFERCOMBO]: string[];
  [CartEntryType.OFFERITEM]: string[];
  [CartEntryType.OFFERDISCOUNT]: string[];
  picker: string[];
}

export const useGetSanityImagesForCartEntries = (cartEntries?: Array<IBackendCartEntries>) => {
  const logger = useLogger();
  const [query, { data: sanityData }] = useLazySanityQuery<IRootQuery>(GetAllImages);
  const { language } = useLocale();
  const [sanityImageMap, setSanityImageMap] = useState<ISanityImageMap>({} as ISanityImageMap);
  useEffect(() => {
    const ids = (cartEntries || []).reduce(
      (acc, item) => {
        try {
          acc[item.type].push(item.sanityId);
          const pickerId = maybeGetPickerIdFromCartEntry(item);
          if (pickerId && !acc.picker.includes(pickerId)) {
            acc.picker.push(pickerId);
          }
        } catch (error) {
          logger.error({
            error,
            message: `Failed to push cartEntry item.type: [${item.type}]. It may be using a new cartEntry type`,
          });
        }
        return acc;
      },
      {
        [CartEntryType.COMBO]: [],
        [CartEntryType.ITEM]: [],
        [CartEntryType.OFFERCOMBO]: [],
        [CartEntryType.OFFERITEM]: [],
        [CartEntryType.OFFERDISCOUNT]: [],
        picker: [],
      } as IIdsMap
    );
    const allOfferIds: string[] = (ids[CartEntryType.OFFERCOMBO] || []).concat(
      ids[CartEntryType.OFFERITEM],
      ids[CartEntryType.OFFERDISCOUNT]
    );
    query({
      variables: {
        comboWhere: {
          _id_in: ids[CartEntryType.COMBO],
        },
        itemWhere: {
          _id_in: ids[CartEntryType.ITEM],
        },
        offerWhere: {
          _id_in: allOfferIds,
        },
        pickerWhere: {
          _id_in: ids.picker,
        },
        rewardWhere: {
          _id_in: ids[CartEntryType.OFFERCOMBO],
        },
      },
    });
  }, [cartEntries, logger, query]);

  useEffect(() => {
    const hasNewImage = Object.values(sanityData ?? {}).some(sanityItems => {
      return sanityItems.some((item: { _id: string }) => isUndefined(sanityImageMap[item._id]));
    });
    if (
      sanityData?.allItems &&
      sanityData?.allCombos &&
      sanityData?.allOffers &&
      sanityData?.allPickers &&
      sanityData?.allRewards &&
      hasNewImage
    ) {
      const entries = [...sanityData.allItems, ...sanityData.allPickers, ...sanityData.allCombos];
      const sanityImageComboItemMap = entries.reduce((acc, item) => {
        acc[item._id] = item.image as ISanityImage;
        return acc;
      }, {} as ISanityImageMap);

      const offers = sanityData.allOffers;
      const offerSanityImageMap = offers.reduce((acc, item) => {
        acc[item._id] = item?.localizedImage?.[language]?.app as ISanityImage;
        return acc;
      }, sanityImageComboItemMap);

      const rewards = sanityData.allRewards;
      const updatedSanityImageMap = rewards.reduce((acc, item) => {
        acc[item._id] = item?.image?.[language] as ISanityImage;
        return acc;
      }, offerSanityImageMap);

      setSanityImageMap(isEmpty(updatedSanityImageMap) ? sanityImageMap : updatedSanityImageMap);
    }
  }, [language, sanityData, sanityImageMap]);
  return sanityImageMap;
};
